// src/context/ReadinessContext.js
// Single shared context for all readiness-gate data.
// Wrap the app with <ReadinessProvider> to avoid duplicate fetches
// across ScreenerView, PortfolioView, and Dashboard components.

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getReadinessGate, getReadinessGateHistory, getReadinessAlerts } from "../api/portfolio";

const ReadinessContext = createContext(null);

export const ReadinessProvider = ({ children, refreshIntervalMs = 60000 }) => {
  const [gate, setGate] = useState(null);
  const [history, setHistory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastFetched, setLastFetched] = useState(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError("");
    Promise.all([
      getReadinessGate().catch(() => null),
      getReadinessGateHistory().catch(() => []),
      getReadinessAlerts().catch(() => []),
    ])
      .then(([gateData, histData, alertsData]) => {
        setGate(gateData);
        setHistory(Array.isArray(histData) ? histData : histData?.history ?? []);
        setAlerts(Array.isArray(alertsData) ? alertsData : alertsData?.alerts ?? []);
        setLastFetched(new Date());
      })
      .catch((err) => {
        setError(err?.message || "Failed to load readiness data.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, refreshIntervalMs);
    return () => clearInterval(id);
  }, [fetchAll, refreshIntervalMs]);

  return (
    <ReadinessContext.Provider value={{ gate, history, alerts, loading, error, lastFetched, refresh: fetchAll }}>
      {children}
    </ReadinessContext.Provider>
  );
};

export const useReadiness = () => {
  const ctx = useContext(ReadinessContext);
  if (!ctx) throw new Error("useReadiness must be used inside <ReadinessProvider>");
  return ctx;
};

export default ReadinessContext;
